import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import { FiArrowUpRight, FiCreditCard, FiFolder, FiGrid, FiHelpCircle, FiLogOut, FiMenu, FiKey, FiShield, FiUser, FiX } from "react-icons/fi";
import { clearPortalSession } from "./PortalSession";
import "./PortalShell.css";

const PortalContext = createContext(null);
export const usePortal = () => useContext(PortalContext);
export const portalApi = process.env.REACT_APP_API_ENDPOINT || "http://localhost:8000/api";

export function projectsFromResponse(data) {
  return (data?.workspaces || []).flatMap((workspace) =>
    (workspace.project_ids || []).map((projectId, index) => ({
      workspaceId: String(workspace.workspace_id),
      projectId: String(projectId),
      name: workspace.project_names?.[index] || `Project ${String(projectId).slice(0, 8)}`,
      workspaceName: workspace.workspace_name || "Workspace",
    }))
  );
}

export function PortalState({ title, children, action }) {
  return <div className="sa-portal-state"><h2>{title}</h2><p>{children}</p>{action}</div>;
}

export function PortalGate({ children, needsProject = false, allowPreview = true }) {
  const { preview, loading, error, reload, project } = usePortal();
  if (preview) return allowPreview ? children : <PortalState title="Account data not loaded">This page requires your workspace session.</PortalState>;
  if (loading) return <PortalState title="Loading your workspace">Retrieving your account and available projects…</PortalState>;
  if (error) return <PortalState title="We couldn’t load your workspace" action={<button className="sa-shell-button" onClick={reload}>Try again</button>}>{error}</PortalState>;
  if (needsProject && !project) return <PortalState title="Your default project is unavailable" action={<button className="sa-shell-button" onClick={reload}>Try again</button>}>Your workspace is set up automatically at Google sign-in. Contact support if it is still unavailable.</PortalState>;
  return children;
}

const links = [
  ["/connect", "Connect", FiKey],
  ["/jobs", "Jobs & Results", FiGrid],
  ["/files", "Files", FiFolder],
  ["/usage", "Usage & Billing", FiCreditCard],
  ["/account", "Account", FiUser],
];

export default function PortalLayout({ demoAccount }) {
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = demoAccount ? "/demo" : "";
  const token = demoAccount ? JSON.stringify(demoAccount.access_token) : localStorage.getItem("accessToken") || "";
  const signedIn = Boolean(token);
  const [sessionExpired, setSessionExpired] = useState(false);
  const account = useMemo(() => { try { return JSON.parse(token) || {}; } catch { return {}; } }, [token]);
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(signedIn);
  const [error, setError] = useState("");
  const [reloadCount, setReloadCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [signOutError, setSignOutError] = useState("");
  const [signingOut, setSigningOut] = useState(false);
  const storageKey = `simatomicProject:${account.user_id || account.email || account.username || "account"}`;
  const reload = useCallback(() => setReloadCount((value) => value + 1), []);
  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    setProjects([]);
    setSelected("");
    setRole("");
    setError("");
    setSessionExpired(false);
    if (!token) { setLoading(false); return undefined; }
    const controller = new AbortController();
    let active = true;
    const timeout = setTimeout(() => controller.abort(), 12000);
    setLoading(true);
    fetch(`${portalApi}/workspace/projects`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal })
      .then(async (response) => {
        if (response.status === 401) { if (active) { setSessionExpired(true); if (!demoAccount) clearPortalSession(); } throw new Error("Your session has expired."); }
        if (!response.ok) throw new Error("The workspace service is unavailable. Please try again.");
        return response.json();
      })
      .then((data) => {
        if (!active) return;
        const nextProjects = projectsFromResponse(data);
        setProjects(nextProjects);
        setRole(data?.role || "");
        const saved = sessionStorage.getItem(storageKey);
        const savedProject = nextProjects.find((item) => `${item.workspaceId}/${item.projectId}` === saved);
        const defaultProject = nextProjects.find((item) => item.workspaceId === data.default_workspace_id && item.projectId === data.default_project_id);
        const next = savedProject || defaultProject || nextProjects[0];
        if (next) setSelected(`${next.workspaceId}/${next.projectId}`);
      })
      .catch((err) => {
        if (active) setError(err.name === "AbortError" ? "The workspace service took too long to respond. Please try again." : err instanceof TypeError ? "Unable to reach the API. Check that your development server is running, then try again." : err.message);
      })
      .finally(() => { clearTimeout(timeout); if (active) setLoading(false); });
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, [token, reloadCount, storageKey, demoAccount]);

  const project = projects.find((item) => `${item.workspaceId}/${item.projectId}` === selected);
  const selectProject = (value) => { setSelected(value); sessionStorage.setItem(storageKey, value); };
  const signOut = async () => {
    setSignOutError("");
    setSigningOut(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`${portalApi}/auth/google/logout`, { method: "POST", credentials: "include", signal: controller.signal });
      if (!response.ok) throw new Error("Server sign-out failed.");
    } catch { setSignOutError("Signed out of this browser. Server sign-out could not be confirmed; sign out on the hosted portal to end that session too."); }
    finally {
      clearTimeout(timeout);
      clearPortalSession();
      navigate("/login", { replace: true });
      setSigningOut(false);
    }
  };
  const scopedPage = location.pathname === `${basePath}/jobs` || location.pathname === `${basePath}/files`;
  const navigationLinks = role === "admin" ? [...links, ["/admin", "Admin", FiShield]] : links;
  if (!signedIn || sessionExpired) return <Navigate to="/login" replace />;
  return <PortalContext.Provider value={{ signedIn, preview: false, account, role, project, projects, loading, error, reload, token, signOut, basePath }}>
    <div className="sa-portal-shell">
      <a className="sa-shell-skip" href="#portal-main">Skip to content</a>
      <aside className={`sa-shell-sidebar${menuOpen ? " sa-shell-sidebar--open" : ""}`} aria-label="Main navigation">
        <Link className="sa-shell-brand" to={`${basePath}/connect`} aria-label="SimAtomic home">SimAtomic</Link>
        <nav aria-label="Portal">{navigationLinks.map(([path, label, Icon]) => <NavLink key={path} to={`${basePath}${path}`} className={({ isActive }) => `sa-shell-nav-link${isActive || (path === "/connect" && location.pathname === "/") ? " is-active" : ""}`}><Icon /><span>{label}</span></NavLink>)}</nav>
        <div className="sa-shell-sidebar-bottom">
          <Link className="sa-shell-utility" to={`${basePath}/help`}><FiHelpCircle />Help</Link>
          {role === "admin" && <Link className="sa-shell-utility" to="/legacy"><FiArrowUpRight />Legacy interface</Link>}
          <a className="sa-shell-support" href="mailto:info@simatomic.com">info@simatomic.com</a>
        </div>
      </aside>
      {menuOpen && <button className="sa-shell-scrim" aria-label="Close menu" onClick={() => setMenuOpen(false)} />}
      <div className="sa-shell-workspace">
        <header className="sa-shell-topbar">
          <button className="sa-shell-mobile-menu" aria-label={menuOpen ? "Close navigation" : "Open navigation"} aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <FiX /> : <FiMenu />}</button>
          <div className="sa-shell-topbar-right">
            {signedIn && <span className="sa-shell-account-name">{demoAccount ? "Website Demo" : account.username || account.email || "Account"}</span>}
            {demoAccount ? <Link className="sa-shell-button" to="/login">Sign in with Google</Link> : <button className="sa-shell-logout" aria-label="Sign out" title="Log out of SimAtomic" disabled={signingOut} onClick={signOut}><FiLogOut />{signingOut ? "Signing out…" : "Logout"}</button>}
          </div>
        </header>
        <main className="sa-shell-main" id="portal-main">
          {signOutError && <p className="sa-shell-alert" role="alert">{signOutError}</p>}
          {scopedPage && signedIn && projects.length > 0 && <div className="sa-shell-project-filter">{projects.length > 1 ? <><label htmlFor="portal-project">Project</label><select id="portal-project" value={selected} onChange={(event) => selectProject(event.target.value)}>{projects.map((item) => <option key={`${item.workspaceId}/${item.projectId}`} value={`${item.workspaceId}/${item.projectId}`}>{item.workspaceName} / {item.name}</option>)}</select></> : <span>{projects[0].workspaceName} / {projects[0].name}</span>}</div>}
          {demoAccount && <p className="sa-demo-intro">You’re exploring the shared Website Demo. You can upload files and explore the portal.</p>}
          <Outlet />
        </main>

      </div>
    </div>
  </PortalContext.Provider>;
}
